import React, {
  useImperativeHandle,
  forwardRef,
  useRef,
  useState,
  useEffect,
} from "react";
import { Row, Col, Button, Form, Modal, Alert } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import {
  createSession,
  fetchPracticeTemplates,
  uploadImage,
  getAppointmentById,
  getAccountByPatientId,
  updateAppointmentStatusByPatientId,
} from "../../api";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import ImageAnnotator from "./EditImages";
import { FaTimes, FaMicrophone, FaStop } from "react-icons/fa";
import { PatientFiles } from "../PatientsAccounts/PatientFiles";
import "../../Components/Communications/Comms.css";

const MediaCaptureSection = ({ mediaImages, setMediaImages, isActive }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startStream = async () => {
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setError(null);
    } catch (e) {
      setError(e.message || "Camera access failed");
    }
  };

  useEffect(() => {
    if (isActive) {
      startStream();
    } else {
      // ensure camera is off when tab not active
      stopStream();
    }
    return stopStream; // also stop on unmount
  }, [isActive, facingMode]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);

    // JPEG keeps size down vs PNG
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setMediaImages((prev) => [dataUrl, ...prev]);
  };

  const removePhoto = (idx) =>
    setMediaImages((prev) => prev.filter((_, i) => i !== idx));

  const flipCamera = () =>
    setFacingMode((m) => (m === "environment" ? "user" : "environment"));

  return (
    <div className="mt-3">
      <Row className="align-items-center mb-3">
        <Col md={12}>
          <h5 className="text-primary fw-bold mb-2">Capture Photos</h5>
          <p className="text-muted mb-0">
            Take one or more photos and they’ll be saved to this session.
          </p>
        </Col>
      </Row>

      {error ? (
        <Alert variant="warning" className="mb-3">
          Camera access failed ({error}). You can still upload from your
          gallery:
          <Form.Control
            className="mt-2"
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              files.forEach((file) => {
                const reader = new FileReader();
                reader.onload = () => {
                  setMediaImages((prev) => [reader.result, ...prev]);
                };
                reader.readAsDataURL(file);
              });
              e.target.value = "";
            }}
          />
        </Alert>
      ) : (
        <div
          className="rounded overflow-hidden shadow-sm mb-3"
          style={{ position: "relative" }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: "100%", height: "auto", background: "#000" }}
          />
          <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center gap-3 p-3">
            <Button
              variant="light"
              className="rounded-circle"
              onClick={capturePhoto}
              aria-label="Capture photo"
              title="Capture"
            >
              <i className="bi bi-camera-fill" />
            </Button>
            <Button
              variant="light"
              className="rounded-circle"
              onClick={flipCamera}
              aria-label="Flip camera"
              title="Flip camera"
            >
              <i className="bi bi-arrow-repeat" />
            </Button>
          </div>
        </div>
      )}

      {mediaImages.length > 0 && (
        <>
          <h6 className="text-primary">Captured ({mediaImages.length})</h6>
          <Row>
            {mediaImages.map((url, idx) => (
              <Col xs={4} sm={3} md={2} key={idx} className="mb-3">
                <div className="position-relative border rounded overflow-hidden">
                  <img
                    src={url}
                    alt={`snap ${idx + 1}`}
                    style={{ width: "100%", height: 100, objectFit: "cover" }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 m-1 p-1"
                    onClick={() => removePhoto(idx)}
                    aria-label="Remove"
                    title="Remove"
                  >
                    ×
                  </Button>
                </div>
              </Col>
            ))}
          </Row>
        </>
      )}
    </div>
  );
};

// --- Voice recorder hook (modal-driven) ---
const useVoiceRecorder = () => {
  const { transcript, resetTranscript, listening } = useSpeechRecognition();

  const start = () => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) return;
    resetTranscript();
    SpeechRecognition.startListening({
      continuous: true,
      interimResults: true,
      language: "en-US",
    });
  };

  const stop = () => {
    SpeechRecognition.stopListening();
    return (transcript || "").trim();
  };

  const clear = () => resetTranscript();

  return { listening, transcript, start, stop, clear };
};

// --- Recorder Modal ---
const RecorderModal = ({ show, onClose, onDone, vr }) => {
  useEffect(() => {
    if (show) vr.clear();
  }, [show]);

  const handleStart = () => vr.start();
  const handleStop = () => vr.stop(); // we only stop; we don't "use" yet
  const handleDone = () => {
    const text = (vr.transcript || "").trim();
    if (vr.listening) vr.stop(); // safety: ensure stopped before closing
    onDone?.(text);
    onClose?.();
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Voice Recorder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center gap-3">
          {/* Soundwave: animates only when vr.listening === true */}
          <div className={`soundwave ${vr.listening ? "live" : ""}`}>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div className="w-100">
            <Form.Label className="fw-semibold">Live transcript</Form.Label>
            <div
              style={{
                minHeight: 90,
                border: "1px solid rgba(0,0,0,.1)",
                borderRadius: 8,
                padding: "0.5rem 0.75rem",
                background: "#fafafa",
                whiteSpace: "pre-wrap",
              }}
            >
              {vr.transcript || (
                <span className="text-muted">Say something…</span>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          {!vr.listening ? (
            <Button variant="outline-primary" onClick={handleStart}>
              <FaMicrophone className="me-2" /> Start
            </Button>
          ) : (
            <Button variant="danger" onClick={handleStop}>
              <FaStop className="me-2" /> Stop
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDone}
            disabled={!vr.transcript?.trim()}
          >
            Use Transcript
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// Template Section Component
const TemplateSection = ({
  sessionTemplate,
  setSessionTemplate,
  dropdownOptions,
  notes,
  setNotes,
  onOpenRecorder,
  errorMessage,
  handleCancel,
  annotatorRefs,
}) => {
  const commitOneImage = (idx) => {
    const ref = annotatorRefs?.current?.[idx];
    if (ref && typeof ref.mergeImage === "function") {
      try {
        const merged = ref.mergeImage(); // should return a data URL string
        if (merged && typeof merged === "string") {
          setNotes((prev) => {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], sessionsImageURL: merged };
            return copy;
          });
        }
      } catch (e) {
        console.warn("mergeImage failed for index", idx, e);
      }
    }
  };

  const handleTemplateChange = (selectedId) => {
    setSessionTemplate(selectedId);

    const selectedTemplate = dropdownOptions
      .flatMap((cat) => cat.options)
      .find((t) => t._id === selectedId);

    if (selectedTemplate && Array.isArray(selectedTemplate.notes)) {
      const updatedNotes = selectedTemplate.notes.map((note) => ({
        ...note,
        sessionsSectionNotes:
          note.sessionsFieldType === "multiselect"
            ? {
                availableOptions: Array.isArray(note.sessionsSectionNotes)
                  ? note.sessionsSectionNotes
                  : [],
                selectedOptions: [],
              }
            : note.sessionsSectionNotes || "",
      }));
      setNotes(updatedNotes);
    } else {
      setNotes([]);
    }
  };

  const updateNoteContent = (index, content) => {
    setNotes((prevNotes) => {
      const updated = [...prevNotes];
      updated[index] = { ...updated[index], sessionsSectionNotes: content };
      return updated;
    });
  };

  const updateMultiselectOptions = (index, selectedOptions) => {
    setNotes((prevNotes) => {
      const updated = [...prevNotes];
      updated[index] = {
        ...updated[index],
        sessionsSectionNotes: {
          ...updated[index].sessionsSectionNotes,
          selectedOptions,
        },
      };
      return updated;
    });
  };

  return (
    <>
      <Row className="align-items-center mb-4 mt-3">
        <Col md={8}>
          <Form.Group
            controlId="formGridSessionTemplate"
            className="d-flex align-items-center mb-0"
          >
            <Form.Label className="me-3 mb-0 text-primary fw-bold">
              Template:
            </Form.Label>
            <Form.Select
              value={sessionTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              <option value="" disabled>
                Choose a template...
              </option>
              {dropdownOptions.map((category, idx) => (
                <React.Fragment key={idx}>
                  <option disabled className="text-primary fw-bold">
                    {category.label}
                  </option>
                  {category.options.map((template) => (
                    <option key={template._id} value={template._id}>
                      {template.templateTitle}
                    </option>
                  ))}
                </React.Fragment>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {sessionTemplate && notes.length > 0 ? (
        <Row className="mt-3">
          {notes.map((note, index) => (
            <Col md={6} key={index} className="mb-4">
              <div className="border rounded p-3 shadow-sm bg-white">
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="text-primary fw-bold mb-0">
                      {note.sessionsSection}
                    </Form.Label>
                    {(note.sessionsFieldType === "text" ||
                      note.sessionsFieldType === "textarea") && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onOpenRecorder(index);
                        }}
                        title="Speak to fill this field"
                      >
                        <FaMicrophone />
                      </Button>
                    )}
                  </div>

                  {note.sessionsFieldType === "text" && (
                    <Form.Control
                      type="text"
                      value={note.sessionsSectionNotes || ""}
                      onChange={(e) => updateNoteContent(index, e.target.value)}
                      placeholder="Enter text here..."
                    />
                  )}

                  {note.sessionsFieldType === "textarea" && (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={note.sessionsSectionNotes || ""}
                      onChange={(e) => updateNoteContent(index, e.target.value)}
                      placeholder="Enter your notes here..."
                    />
                  )}

                  {note.sessionsFieldType === "multiselect" &&
                    note.sessionsSectionNotes &&
                    Array.isArray(
                      note.sessionsSectionNotes.availableOptions
                    ) && (
                      <div>
                        {note.sessionsSectionNotes.availableOptions.map(
                          (option, i) => (
                            <Form.Check
                              key={i}
                              type="checkbox"
                              label={option}
                              checked={(
                                note.sessionsSectionNotes.selectedOptions || []
                              ).includes(option)}
                              onChange={(e) => {
                                const currentSelected =
                                  note.sessionsSectionNotes.selectedOptions ||
                                  [];
                                const updatedSelected = e.target.checked
                                  ? [...currentSelected, option]
                                  : currentSelected.filter(
                                      (opt) => opt !== option
                                    );
                                updateMultiselectOptions(
                                  index,
                                  updatedSelected
                                );
                              }}
                            />
                          )
                        )}
                      </div>
                    )}

                  {note.sessionsFieldType === "image" && (
                    <div className="mt-2">
                      <div className="annotator-scroll">
                        <ImageAnnotator
                          ref={(el) => (annotatorRefs.current[index] = el)}
                          imageUrl={note.sessionsImageURL}
                          onUpdateImage={(newUrl) => {
                            // if your annotator emits incremental updates, keep them
                            setNotes((prev) => {
                              const copy = [...prev];
                              copy[index] = {
                                ...copy[index],
                                sessionsImageURL: newUrl,
                              };
                              return copy;
                            });
                          }}
                        />
                      </div>
                      <div className="mt-2 d-flex gap-2 justify-content-center">
                        <Button
                          className="rounded-0"
                          variant="outline-primary"
                          size="sm"
                          onClick={() => commitOneImage(index)}
                        >
                          Apply edits
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.Group>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <div></div>
      )}
    </>
  );
};

// Notepad Section Component
const NotepadSection = ({
  notepadContent,
  setNotepadContent,
  onOpenNotepadRecorder,
}) => {
  const quillRef = useRef(null);

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
    ],
  };

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "list",
    "bullet",
    "align",
    "link",
  ];

  return (
    <div className="mt-3">
      <Row className="align-items-center mb-3">
        <Col md={12}>
          <h5 className="text-primary fw-bold mb-0">Quick Notes</h5>

          <div className="d-flex align-items-center gap-2">
            <p className="text-muted mb-0 mt-1">
              Use this space for quick notes during the session.
            </p>
            <Button
              variant="outline-primary"
              className="ms-1 fs-6 border-0"
              aria-label="Speak to add to notepad"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenNotepadRecorder();
              }}
              title="Speak to add to notepad"
            >
              <FaMicrophone />
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <div className="bg-white rounded shadow-sm overflow-hidden notepad-container">
            <ReactQuill
              ref={quillRef}
              value={notepadContent}
              onChange={setNotepadContent}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Start typing your notes here..."
              style={{
                minHeight: "200px", // Increased from 300px
                height: "300px", // Fixed height for consistent notepad feel
              }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

// Media Section Component
const MediaSection = ({ patientId }) => (
  <div className="mt-3">
    <Row className="align-items-center mb-3">
      <Col md={12}>
        <h5 className="text-primary fw-bold mb-2">Media & Files</h5>
        <p className="text-muted mb-0">
          Attach or view media files related to this appointment.
        </p>
      </Col>
    </Row>

    <Row>
      <Col md={12}>
        <div className="bg-white rounded p-3 shadow-sm">
          <PatientFiles patientId={patientId} />
        </div>
      </Col>
    </Row>
  </div>
);

// Submit Button Component
const SubmitButton = ({ onClick, loading = false }) => (
  <div className="d-flex justify-content-center mt-4">
    <Button
      variant="primary"
      size="lg"
      onClick={onClick}
      disabled={loading}
      className="d-flex align-items-center justify-content-center gap-2 px-4 py-2 rounded-0"
    >
      <i className="fa-regular fa-floppy-disk"></i>
      {loading ? "Processing..." : "Submit All Sections"}
    </Button>
  </div>
);

// Main Component
export const FollowUpSession = forwardRef(
  (
    {
      appointmentId,
      patientId,
      serviceName,
      resourceId,
      appointmentStart,
      appointmentStatus,
      practiceId,
      onUpdate,
      activeSection,
      adHoc = false,
    },
    ref
  ) => {
    // State management
    const [successAlert, setSuccessAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showRotateMessage, setShowRotateMessage] = useState(false);
    const [mediaImages, setMediaImages] = useState([]);

    // Data state
    const [appointment, setAppointment] = useState(null);
    const [account, setAccount] = useState({});
    const [templates, setTemplates] = useState([]);
    const [dropdownOptions, setDropdownOptions] = useState([]);

    // Section-specific state
    const [sessionTemplate, setSessionTemplate] = useState("");
    const [notes, setNotes] = useState([]);
    const [notepadContent, setNotepadContent] = useState("");
    const annotatorRefs = useRef([]); // { [index: number]: ImageAnnotatorRef }

    const navigate = useNavigate();

    // Imperative handle for external completion
    useImperativeHandle(ref, () => ({
      async handleExternalCompletion() {
        if (!sessionTemplate && activeSection === "apptTemplate") {
          return false;
        }
        await handleSubmit();
        return true;
      },
    }));

    // Initialize data
    useEffect(() => {
      const initializeData = async () => {
        const token = sessionStorage.getItem("user");
        if (!token) return;

        try {
          const decoded = jwtDecode(token);
          const userPractices = decoded.practice || [];

          const promises = [
            fetchPracticeTemplates(decoded._id),
            getAccountByPatientId(patientId),
          ];

          // Only fetch appointment data if not ad-hoc
          if (!adHoc && appointmentId) {
            promises.push(getAppointmentById(appointmentId));
          }

          const [templatesData, accountData, appointmentData] =
            await Promise.all(promises);

          setAccount(accountData);

          // Build template buckets
          const filteredTemplates = (templatesData || []).filter((t) =>
            t.templatePracticeId?.some((pid) => userPractices.includes(pid))
          );
          const initialTemplates = filteredTemplates.filter(
            (t) => t.templateInitial === "initial"
          );
          const followUpTemplates = filteredTemplates.filter(
            (t) => t.templateInitial === "followup"
          );

          setDropdownOptions([
            { label: "Initial Assessment", options: initialTemplates },
            { label: "Follow Up", options: followUpTemplates },
          ]);
          setTemplates(filteredTemplates);

          // Only set appointment state in appointment mode
          if (!adHoc && appointmentData) {
            setAppointment(appointmentData);
          }
        } catch (error) {
          console.error("Error initializing data:", error);
        }
      };

      initializeData();
    }, [appointmentId, patientId, adHoc]);

    // inside FollowUpSession component:
    const vr = useVoiceRecorder(); // use the new hook

    // Which target opened the recorder
    // { type: 'template', index: number } | { type: 'notepad' }
    const [recTarget, setRecTarget] = useState(null);
    const [recOpen, setRecOpen] = useState(false);

    const openRecorderForTemplate = (index) => {
      setRecTarget({ type: "template", index });
      setRecOpen(true);
    };
    const openRecorderForNotepad = () => {
      setRecTarget({ type: "notepad" });
      setRecOpen(true);
    };
    const closeRecorder = () => {
      // ensure we stop if still listening
      if (vr.listening) vr.stop();
      setRecOpen(false);
      setRecTarget(null);
    };

    const applyTranscriptToTarget = (text) => {
      const cleaned = (text || "").trim();
      if (!cleaned) return;

      if (recTarget?.type === "template" && Number.isInteger(recTarget.index)) {
        // append to the chosen text/textarea note
        setNotes((prev) => {
          const updated = [...prev];
          const idx = recTarget.index;
          const raw = updated[idx]?.sessionsSectionNotes;
          const current = typeof raw === "string" ? raw : (raw ?? "") + "";
          updated[idx] = {
            ...updated[idx],
            sessionsSectionNotes: current ? `${current} ${cleaned}` : cleaned,
          };
          return updated;
        });
      } else if (recTarget?.type === "notepad") {
        // append to notepad
        setNotepadContent((prev) => {
          const base = (prev || "").toString();
          return base ? `${base} ${cleaned}` : cleaned;
        });
      }
    };

    // keep refs to each ImageAnnotator so we can merge edits on demand

    useEffect(() => {
      // keep refs array in sync with notes length
      annotatorRefs.current = annotatorRefs.current.slice(0, notes.length);
    }, [notes.length]);

    // commit ALL image edits (used before leaving the tab and before saving)
    const commitAllImages = () => {
      let changed = false;
      const copy = [...notes];
      copy.forEach((note, idx) => {
        if (note?.sessionsFieldType === "image") {
          const ref = annotatorRefs.current[idx];
          if (ref && typeof ref.mergeImage === "function") {
            try {
              const merged = ref.mergeImage();
              if (
                merged &&
                typeof merged === "string" &&
                merged !== note.sessionsImageURL
              ) {
                copy[idx] = { ...note, sessionsImageURL: merged };
                changed = true;
              }
            } catch (e) {
              console.warn("mergeImage failed for index", idx, e);
            }
          }
        }
      });
      if (changed) setNotes(copy);
    };

    useEffect(() => {
      if (activeSection !== "apptTemplate") {
        commitAllImages();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    // Handle orientation change for mobile
    useEffect(() => {
      const handleOrientationChange = () => {
        if (window.innerWidth > window.innerHeight && showRotateMessage) {
          setShowRotateMessage(false);
          handleSubmit();
        }
      };

      window.addEventListener("resize", handleOrientationChange);
      return () =>
        window.removeEventListener("resize", handleOrientationChange);
    }, [showRotateMessage]);

    const createInvoiceObject = () => ({
      invoiceDate: new Date().toISOString().slice(0, 10),
      invoiceCreatedDate: new Date().toISOString(),
      accountId: account?._id,
      accountName: `${account?.accountFirstName || ""} ${
        account?.accountLastName || ""
      }`.trim(),
      accountEmail: account?.accountEmail || "",
      patient: {
        patientId,
        patientFirstName: appointment?.patientFirstName || "",
        patientLastName: appointment?.patientLastName || "",
        patientMedicalAid: account?.accountMedicalAid || "",
        patientMedicalAidNumber: account?.accountMedicalAidNumber || "",
        patientMedicalAidOptionCode: account?.accountMedicalAidOptionCode || "",
        patientMedicalAidPlanCode: account?.accountMedicalAidPlanCode || "",
        patientMedicalAidDependantCode: account?.accountDependantCode || "",
      },
      placeOfService: "11",
      inPatientCase: {
        caseAuthNumber: "",
        caseBHF: "",
        caseHPCSA: "",
        caseICD10: "",
      },
      outPatient: "O",
      practice: {
        practiceNumber: account?.accountPracticeNumber,
        practiceName: account?.accountPracticeName,
        vatNumber: account?.accountPracticeVAT || "",
      },
      practitioner: {
        id: appointment?.practitionerId,
        firstName: appointment?.practitionerFirstName,
        lastName: appointment?.practitionerLastName,
        hpcsa: appointment?.practitionerHPCSA,
      },
      lines: [],
      medicalAid: {
        name: account?.accountMedicalAid,
        number: account?.accountMedicalAidNumber,
        optionCode: account?.accountMedicalAidOptionCode,
        plan: account?.accountMedicalAidPlan,
        option: account?.accountMedicalAidOption,
      },
      totalAmount: 0,
      totalPatientAmount: 0,
      totalMedicalAidAmount: 0,
      medAidTotal: 0,
      patientTotal: 0,
      outstandingBalance: {
        patientOutstanding: 0,
        medAidOutstanding: 0,
        totalOutstanding: 0,
        paidAmount: 0,
      },
      status: "Draft",
      appointmentDate: appointment?.start
        ? new Date(appointment.start).toISOString().split("T")[0]
        : "",
    });

    // Updated handleSubmit method for FollowUpSession component

    const handleSubmit = async () => {
      // Keep the rotate check only for appointment mode
      const isPortraitNow = window.innerHeight > window.innerWidth;
      if (!adHoc && window.innerWidth < 768 && isPortraitNow) {
        setShowRotateMessage(true);
        return;
      }

      // 🔹 Resolve practice and patient IDs robustly
      const resolvedPatientId = String(patientId || "").trim();
      // Try in this order: prop -> account.practiceNumber-ish -> first practice from JWT
      let resolvedPracticeId =
        (practiceId != null && String(practiceId)) ||
        (account?.accountPracticeNumber &&
          String(account.accountPracticeNumber)) ||
        null;

      if (!resolvedPracticeId) {
        try {
          const token = sessionStorage.getItem("user");
          if (token) {
            const decoded = jwtDecode(token);
            // decoded.practice could be: array of practice IDs or numbers
            if (
              Array.isArray(decoded.practice) &&
              decoded.practice.length > 0
            ) {
              resolvedPracticeId = String(decoded.practice[0]);
            }
          }
        } catch {}
      }

      if (!resolvedPatientId || !resolvedPracticeId) {
        setErrorMessage(
          "Missing Patient or Practice. Please wait a moment and try again."
        );
        return;
      }

      setIsSaving(true);
      try {
        // A) Merge inline annotator edits first
        commitAllImages();

        // B) Upload template/annotator images (convert data URLs only)
        const processedNotes = [];
        for (let i = 0; i < (notes || []).length; i++) {
          const n = notes[i];
          if (n.sessionsFieldType !== "image") {
            processedNotes.push(n);
            continue;
          }

          const url =
            typeof n.sessionsImageURL === "string" ? n.sessionsImageURL : "";
          const isDataUrl = url.startsWith("data:");

          if (url && isDataUrl) {
            const blob = await fetch(url).then((r) => r.blob());
            const formData = new FormData();
            formData.append("image", blob);
            formData.append("x-image-caption", "Session Image Upload");
            formData.append("patientId", resolvedPatientId);
            if (!adHoc && appointmentId)
              formData.append("appointmentId", appointmentId);
            const uploadResp = await uploadImage(formData);
            processedNotes.push({
              ...n,
              sessionsImageURL: uploadResp.imageUrl,
            });
          } else {
            processedNotes.push(n);
          }
        }

        // C) Upload media photos (convert data URLs only)
        const uploadedMediaFiles = [];
        for (let idx = 0; idx < (mediaImages || []).length; idx++) {
          const m = mediaImages[idx];
          if (typeof m !== "string") continue;

          if (m.startsWith("http://") || m.startsWith("https://")) {
            uploadedMediaFiles.push({
              fileName: `media-photo-${idx + 1}`,
              fileUrl: m,
              fileType: "image",
              uploadedAt: new Date().toISOString(),
              description: `Captured photo ${idx + 1}`,
            });
            continue;
          }

          if (m.startsWith("data:")) {
            const blob = await fetch(m).then((r) => r.blob());
            const formData = new FormData();
            formData.append("image", blob);
            formData.append("x-image-caption", `Media Photo ${idx + 1}`);
            formData.append("patientId", resolvedPatientId);
            if (!adHoc && appointmentId)
              formData.append("appointmentId", appointmentId);
            const uploadResp = await uploadImage(formData);
            uploadedMediaFiles.push({
              fileName: `media-photo-${idx + 1}`,
              fileUrl: uploadResp.imageUrl,
              fileType: "image",
              uploadedAt: new Date().toISOString(),
              description: `Captured photo ${idx + 1}`,
            });
          }
        }

        // D) De-dup media by URL
        const seen = new Set();
        const dedupedMediaFiles = uploadedMediaFiles.filter((f) => {
          if (seen.has(f.fileUrl)) return false;
          seen.add(f.fileUrl);
          return true;
        });

        // E) Build payload (omit appointment fields in ad-hoc mode)
        const sessionsInitial = sessionTemplate
          ? templates.find((t) => t._id === sessionTemplate)
              ?.templateInitial === "initial"
          : false;
        const nowIso = new Date().toISOString();

        const payload = {
          sessionsPatientId: resolvedPatientId,
          sessionsPracticeId: resolvedPracticeId,
          ...(adHoc
            ? {
                sessionsAdHoc: true,
                // ⬇️ ensure ad-hoc notes have a timestamp
                sessionsAppointmentStart: appointmentStart || nowIso,
              }
            : {
                sessionsAppointmentId: appointmentId,
                sessionsServiceName: serviceName,
                sessionsResourceId: resourceId,
                sessionsAppointmentStart: appointmentStart || nowIso, // extra guard for appt too
              }),
          ...(sessionTemplate
            ? { sessionsTemplateId: sessionTemplate, sessionsInitial }
            : {}),
          ...(processedNotes.length
            ? {
                sections: processedNotes.map((note) => ({
                  sessionsSection: note.sessionsSection,
                  sessionsFieldType: note.sessionsFieldType,
                  sessionsSectionNotes:
                    note.sessionsFieldType === "multiselect"
                      ? {
                          availableOptions:
                            note.sessionsSectionNotes?.availableOptions || [],
                          selectedOptions:
                            note.sessionsSectionNotes?.selectedOptions || [],
                        }
                      : typeof note.sessionsSectionNotes === "string"
                      ? note.sessionsSectionNotes
                      : "",
                  sessionsImageURL:
                    note.sessionsFieldType === "image" &&
                    typeof note.sessionsImageURL === "string"
                      ? note.sessionsImageURL
                      : "",
                })),
              }
            : {}),
          ...(notepadContent?.trim()
            ? { notepadContent, notepadContentType: "html" }
            : {}),
          ...(dedupedMediaFiles.length
            ? { mediaFiles: dedupedMediaFiles }
            : {}),
        };

        // F) Persist
        await createSession(payload); // <- works for both appointment + ad-hoc

        const isUnregistered = String(resolvedPatientId).trim() === "1";
       if (isUnregistered) {
         setSuccessAlert(true);
         // (optional) clear local state here if you want:
         // setSessionTemplate(""); setNotes([]); setNotepadContent(""); setMediaImages([]);
         navigate("/bookings");
         return;
       }

        if (adHoc) {
          // Ad-hoc: no status updates, no invoice navigation
          setSuccessAlert(true);
          // optional clear:
          // setSessionTemplate(""); setNotes([]); setNotepadContent(""); setMediaImages([]);
          onUpdate?.(); // parent flips to "Previous Notes"
          return;
        }

        // G) Appointment flow stays as-is
        try {
          await updateAppointmentStatusByPatientId(
            appointmentId,
            "Completed",
            "System"
          );
        } catch {}
        const invoiceObject = createInvoiceObject();
        if (appointment.isInvoiced) {
          navigate("/bookings");
          return;
        }
        let appt = appointment;
        navigate(`/invoices/1/${account._id}`, {
          state: {
            // service to pre-select and expand
            servicesId: appt?.serviceId,

            // appointment linking
            appointmentId: appt?._id || appt?.id,
            appointmentStart: appt?.start || appt?.startTime || null, // ISO if available
            appointmentEnd: appt?.end || appt?.endTime || null, // ISO if available
          

            // who/what
            patientId: appt?.patientId,
            practiceId: appt?.practiceId || resolvedPracticeId,
            practitionerId:
              appt?.practitionerId || appt?.userId || appt?.resourceId || null,
          },
        });
      } catch (error) {
        console.error("Error saving session:", error);
        setErrorMessage(
          "An error occurred while saving the session. Please try again."
        );
      } finally {
        setIsSaving(false);
      }
    };

    const handleCancel = () => {
      setErrorMessage(false);
    };

    // Render methods
    const renderCurrentSection = () => {
      switch (activeSection) {
        case "apptNotepad":
          return (
            <>
              <NotepadSection
                notepadContent={notepadContent}
                setNotepadContent={setNotepadContent}
                onOpenNotepadRecorder={openRecorderForNotepad}
              />
              <SubmitButton
                onClick={handleSubmit}
                loading={isSaving || (!adHoc ? false : !account && !practiceId)}
              />
            </>
          );

        case "apptMedia":
          return (
            <>
              <MediaCaptureSection
                mediaImages={mediaImages}
                setMediaImages={setMediaImages}
                isActive={activeSection === "apptMedia"}
              />
              <SubmitButton
                onClick={handleSubmit}
                loading={isSaving || (!adHoc ? false : !account && !practiceId)}
              />
            </>
          );

        default: // apptTemplate
          return (
            <>
              <TemplateSection
                sessionTemplate={sessionTemplate}
                setSessionTemplate={setSessionTemplate}
                dropdownOptions={dropdownOptions}
                notes={notes}
                setNotes={setNotes}
                onOpenRecorder={openRecorderForTemplate}
                errorMessage={errorMessage}
                handleCancel={handleCancel}
                annotatorRefs={annotatorRefs}
              />
              <SubmitButton
                onClick={handleSubmit}
                loading={isSaving || (!adHoc ? false : !account && !practiceId)}
              />
            </>
          );
      }
    };

    return (
      <div className="followup-session-container">
        {successAlert && (
          <Alert variant="success" className="mb-4">
            The session was saved successfully and invoice created.
          </Alert>
        )}

        {renderCurrentSection()}

        <RecorderModal
          show={recOpen}
          onClose={closeRecorder}
          onDone={applyTranscriptToTarget}
          vr={vr}
        />

        {/* Mobile rotation message */}
        {showRotateMessage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.85)",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              padding: "2rem",
              textAlign: "center",
            }}
          >
            <h4>Please rotate your device</h4>
            <p>This screen works best in landscape orientation.</p>
          </div>
        )}
      </div>
    );
  }
);

export default FollowUpSession;
